// FTEDN Team — Waitlist Management Page
import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../hooks/useApi';
import './WaitlistMgmt.css';

type WaitlistStatus = 'pending' | 'approved' | 'rejected';
type WaitlistStatusFilter = WaitlistStatus | 'all';

interface WaitlistEntry {
  id: string;
  name: string;
  email: string;
  status: WaitlistStatus;
  appliedAt?: string;
  referralSource?: string;
  sourceHost?: string;
  nextStep?: string;
}

interface WaitlistResponse {
  success?: boolean;
  data?: WaitlistEntry[];
  meta?: {
    hasNext?: boolean;
    nextCursor?: string | null;
  };
}

function formatAppliedAt(value?: string): string {
  if (!value) return 'unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

const REVIEW_NOTE_MIN_LENGTH = 5;
const REVIEW_NOTE_ERROR = 'A review note (5+ chars) is required';
const statusFilters: WaitlistStatusFilter[] = ['all', 'pending', 'approved', 'rejected'];

function getStatusFilterLabel(filter: WaitlistStatusFilter): string {
  return filter === 'all' ? 'All' : filter.charAt(0).toUpperCase() + filter.slice(1);
}

function getSearchText(entry: WaitlistEntry): string {
  return [
    entry.name,
    entry.email,
    entry.referralSource,
    entry.sourceHost,
    entry.nextStep,
  ].filter(Boolean).join(' ').toLowerCase();
}

export function WaitlistMgmtPage() {
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [actingId, setActingId] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [statusFilter, setStatusFilter] = useState<WaitlistStatusFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [reviewNoteDrafts, setReviewNoteDrafts] = useState<Record<string, string>>({});
  const [reviewNoteErrors, setReviewNoteErrors] = useState<Record<string, string>>({});

  const loadEntries = useCallback(async (cursor?: string) => {
    setError('');
    if (cursor) setLoadingMore(true);
    else setLoading(true);

    try {
      const path = cursor ? `/waitlist?cursor=${encodeURIComponent(cursor)}&pageSize=100` : '/waitlist';
      const response = await apiClient.get(path) as WaitlistResponse;
      const nextEntries = Array.isArray(response.data) ? response.data : [];

      setEntries((current) => cursor ? [...current, ...nextEntries] : nextEntries);
      setNextCursor(response.meta?.nextCursor ?? null);
      setHasMore(Boolean(response.meta?.hasNext && response.meta.nextCursor));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load waitlist');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    void loadEntries();
  }, [loadEntries]);

  const stats = useMemo(() => ({
    pending: entries.filter((entry) => entry.status === 'pending').length,
    approved: entries.filter((entry) => entry.status === 'approved').length,
    rejected: entries.filter((entry) => entry.status === 'rejected').length,
  }), [entries]);

  const filteredEntries = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return entries.filter((entry) => {
      const matchesStatus = statusFilter === 'all' || entry.status === statusFilter;
      const matchesSearch = !normalizedSearch || getSearchText(entry).includes(normalizedSearch);
      return matchesStatus && matchesSearch;
    });
  }, [entries, searchTerm, statusFilter]);

  async function reviewEntry(entry: WaitlistEntry, action: 'approve' | 'reject') {
    const notes = (reviewNoteDrafts[entry.id] ?? '').trim();
    if (notes.length < REVIEW_NOTE_MIN_LENGTH) {
      setReviewNoteErrors((current) => ({ ...current, [entry.id]: REVIEW_NOTE_ERROR }));
      return;
    }

    setActingId(entry.id);
    setError('');
    setReviewNoteErrors((current) => ({ ...current, [entry.id]: '' }));
    try {
      await apiClient.patch(`/waitlist/${entry.id}/${action}`, { notes });
      setReviewNoteDrafts((current) => ({ ...current, [entry.id]: '' }));
      await loadEntries();
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${action} applicant`);
    } finally {
      setActingId(null);
    }
  }

  return (
    <div className="mgmt-page">
      <div className="mgmt-header">
        <h1>Waitlist</h1>
        <p className="mgmt-subtitle">Review and manage applicants</p>
        <div className="mgmt-stats">
          <span className="stat-chip">{stats.pending} Pending</span>
          <span className="stat-chip approved">{stats.approved} Approved</span>
          <span className="stat-chip rejected">{stats.rejected} Rejected</span>
        </div>
      </div>

      {error && <div className="mgmt-error" role="alert">{error}</div>}

      <div className="waitlist-controls">
        <div className="waitlist-filter-group" role="group" aria-label="Waitlist status filter">
          {statusFilters.map((filter) => (
            <button
              key={filter}
              type="button"
              className={`waitlist-filter-btn ${statusFilter === filter ? 'active' : ''}`}
              aria-pressed={statusFilter === filter}
              onClick={() => setStatusFilter(filter)}
            >
              {getStatusFilterLabel(filter)}
            </button>
          ))}
        </div>
        <input
          type="search"
          className="waitlist-search"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          placeholder="Search applicants"
          aria-label="Search waitlist applicants"
        />
      </div>

      <div className="mgmt-table">
        {loading && <div className="mgmt-empty">Loading waitlist...</div>}
        {!loading && entries.length === 0 && <div className="mgmt-empty">No waitlist applicants yet.</div>}
        {!loading && entries.length > 0 && filteredEntries.length === 0 && (
          <div className="mgmt-empty">No applicants match the current filters.</div>
        )}
        {filteredEntries.map(e => {
          const reviewNoteInputId = `waitlist-review-note-${e.id}`;
          const reviewNoteError = reviewNoteErrors[e.id];
          return (
            <div key={e.id} className="mgmt-row">
              <div className="mgmt-row-info">
                <strong>{e.name}</strong>
                <span>{e.email}</span>
                {(e.referralSource || e.sourceHost || e.nextStep) && (
                  <small>
                    {[e.referralSource || e.sourceHost, e.nextStep].filter(Boolean).join(' · ')}
                  </small>
                )}
              </div>
              <span className="mgmt-time">{formatAppliedAt(e.appliedAt)}</span>
              {e.status === 'pending' ? (
                <>
                  <div className="mgmt-status-reason">
                    <label className="mgmt-reason-label" htmlFor={reviewNoteInputId}>Review note</label>
                    <input
                      id={reviewNoteInputId}
                      className="mgmt-reason-input"
                      value={reviewNoteDrafts[e.id] ?? ''}
                      disabled={actingId === e.id}
                      onChange={(event) => {
                        setReviewNoteDrafts((current) => ({ ...current, [e.id]: event.target.value }));
                        if (reviewNoteError) {
                          setReviewNoteErrors((current) => ({ ...current, [e.id]: '' }));
                        }
                      }}
                      placeholder="Review note"
                      aria-label={`Review note for ${e.name}`}
                      aria-invalid={reviewNoteError ? 'true' : 'false'}
                      aria-describedby={reviewNoteError ? `${reviewNoteInputId}-error` : undefined}
                    />
                    {reviewNoteError && (
                      <p id={`${reviewNoteInputId}-error`} className="mgmt-action-error" role="alert">
                        {reviewNoteError}
                      </p>
                    )}
                  </div>
                  <div className="mgmt-actions">
                    <button
                      className="action-btn approve"
                      disabled={actingId === e.id}
                      onClick={() => void reviewEntry(e, 'approve')}
                      aria-label={`Approve ${e.name}`}
                    >
                      Approve
                    </button>
                    <button
                      className="action-btn reject"
                      disabled={actingId === e.id}
                      onClick={() => void reviewEntry(e, 'reject')}
                      aria-label={`Reject ${e.name}`}
                    >
                      Reject
                    </button>
                  </div>
                </>
              ) : (
                <span className={`status-badge ${e.status}`}>{e.status}</span>
              )}
            </div>
          );
        })}
        {hasMore && nextCursor && (
          <button
            className="waitlist-load-more"
            type="button"
            disabled={loadingMore}
            onClick={() => void loadEntries(nextCursor)}
          >
            {loadingMore ? 'Loading...' : 'Load more'}
          </button>
        )}
      </div>
    </div>
  );
}
